import { Injectable } from '@angular/core';
import {
	HttpRequest, HttpHandler, HttpEvent,
	HttpInterceptor, HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, from, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { OAuthService } from 'angular-oauth2-oidc';
import { SnackbarService } from './snackbar.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
	private isRefreshing = false;
	private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

	constructor(
		private readonly oauthService: OAuthService,
		public _snackbarService: SnackbarService
	) { }

	intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		const token = this.oauthService.getAccessToken();
		let req = request;

		if (token) {
			req = this.addTokenToRequest(request, token);
		}

		return next.handle(req).pipe(
			catchError((err: HttpErrorResponse) => {
				if (err.status === 401) {
					return this.handle401Error(request, next, err);
				}
				return throwError(() => err);
			})
		);
	}

	private handle401Error(request: HttpRequest<unknown>, next: HttpHandler, err: HttpErrorResponse): Observable<HttpEvent<unknown>> {
		if (!this.isRefreshing) {
			this.isRefreshing = true;
			this.refreshTokenSubject.next(null);

			return from(this.oauthService.refreshToken()).pipe(
				switchMap(() => {
					this.isRefreshing = false;
					const newToken = this.oauthService.getAccessToken();
					if (newToken) {
						this.refreshTokenSubject.next(newToken);
						return next.handle(this.addTokenToRequest(request, newToken));
					} else {
						this.refreshTokenSubject.next(null);
						this.logout();
						return throwError(() => err);
					}
				}),
				catchError(refreshError => {
					this.isRefreshing = false;
					this.refreshTokenSubject.next(null);
					this.logout();
					return throwError(() => refreshError);
				})
			);
		} else {
			// Wait for the ongoing refresh to complete, with a timeout to avoid hanging forever
			return this.refreshTokenSubject.pipe(
				filter(token => token !== null),
				take(1),
				switchMap(token => {
					if (token) {
						return next.handle(this.addTokenToRequest(request, token));
					} else {
						return throwError(() => err);
					}
				})
			);
		}
	}

	private addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
		return request.clone({
			setHeaders: {
				Authorization: `Bearer ${token}`,
			},
		});
	}

	private logout(): void {
		const idToken = this.oauthService.getIdToken();

		if (idToken) {
			this.oauthService.logOut({
				idTokenHint: idToken,
				postLogoutRedirectUri: window.location.origin,
			});
		} else {
			// Just clear local tokens and redirect manually
			this.oauthService.logOut(); // Clears local storage and tokens
			window.location.href = window.location.origin;
		}

		this._snackbarService.notify('Session expired. Please login again.', 2);
	}


}
